import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Library, UploadCloud, FileText, Trash2, Loader2, Presentation } from "lucide-react";
import { toast } from "sonner";

export function MaterialsSection({ assignmentId }) {
  const inputRef = useRef(null);
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/assignments/${assignmentId}/materials`).then(({ data }) => setMaterials(data)).catch(() => {});
  useEffect(() => { load(); }, [assignmentId]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/assignments/${assignmentId}/materials`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${file.name} added to course materials.`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    setBusy(false);
    load();
  };

  const remove = async (mid) => {
    try { await api.delete(`/assignments/${assignmentId}/materials/${mid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="materials-section">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]"><Library className="h-5 w-5" /></div>
        <div>
          <h3 className="font-display font-semibold">Course materials</h3>
          <p className="text-sm text-muted-foreground">Upload what your professor gave you — slides, PDFs, textbook chapters, reading lists. We ground your blueprint, evaluation and chat in these.</p>
        </div>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        className="mt-4 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-background/60 px-4 py-6 text-center">
        <UploadCloud className="h-6 w-6 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Drag &amp; drop one or more files, or</p>
        <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.pptx,.txt,.md,.csv" className="hidden" data-testid="materials-file-input"
          onChange={(e) => upload(e.target.files)} />
        <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="materials-choose-file">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Choose files
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">PDF, DOCX, PPTX, TXT · lecture slides, readings, notes</p>
      </div>

      {materials.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="materials-list">
          {materials.map((m) => (
            <li key={m.id} data-testid={`material-${m.id}`} className="flex items-center justify-between rounded-xl border border-border bg-background/50 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                {m.name?.toLowerCase().endsWith(".pptx") ? <Presentation className="h-4 w-4 shrink-0 text-[#D97706]" /> : <FileText className="h-4 w-4 shrink-0 text-[#0B5D3B] dark:text-[#25D366]" />}
                <span className="truncate text-sm font-medium">{m.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">· {m.word_count} words</span>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(m.id)} data-testid={`material-delete-${m.id}`}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
